import { useMemo } from "react";

import { OptionsSelectTextField } from "@components/TextFieldSelect";

import { useForm } from "../context/FormContext";
import { IProductCard } from "../../Model";

import { capitalizeFirstLetterFullString } from "@utils/index";
import useDialogAlert from "@hooks/useDialogAlert";

function genericsConditionsValidate<T>(
  value: T | null
): value is NonNullable<T> {
  return value !== null;
}

export const useRequestFormValidation = () => {
  const {
    productCart,
    selectedAddress,
    dataCustomer,
    selectedFilial,
  } = useForm();

  const { snackbar } = useDialogAlert();

  const isCartValid = (): boolean => {
    if (productCart?.length === 0) {
      snackbar({
        message: "Carrinho de produtos vazio!",
        variant: "error",
      });
      return false;
    }

    const productWithZeroQuantity = productCart?.find((product) => {
      const quantity = parseFloat(product.amount?.replace(".", "") || "0");
      return quantity < 1;
    });

    if (productWithZeroQuantity) {
      snackbar({
        message: `Você deve inserir quantidade diferente de zero para o produto ${capitalizeFirstLetterFullString(
          productWithZeroQuantity.name ?? ""
        )}`,
        variant: "warning",
      });
      return false;
    }

    return true;
  };

  const isFormContextValid = (): boolean => {
    if (!selectedAddress) {
      snackbar({
        message: "Endereço de entrega não selecionado!",
        variant: "error",
      });
      return false;
    }

    if (!selectedFilial) {
      snackbar({ message: "Filial não selecionada!", variant: "error" });
      return false;
    }

    if (!dataCustomer) {
      snackbar({
        message: "Dados do cliente não encontrados!",
        variant: "error",
      });
      return false;
    }

    return true;
  };

  return {
    isCartValid,
    isFormContextValid,
    isDataCustomerValid: genericsConditionsValidate,
    isSelectedFilialValid: genericsConditionsValidate,
    isSelectedAddressValid: genericsConditionsValidate,
  };
};

interface Params {
  selectedAddress: OptionsSelectTextField | null;
  selectedAmount: string;
  productCart: IProductCard[];
}

interface SubmitAvailability {
  isDisabled: boolean;
  reason: string | null;
}

export const useSubmitAvailability = ({
  selectedAddress,
  selectedAmount,
  productCart,
}: Params): SubmitAvailability => {
  return useMemo(() => {
    if (!selectedAddress) {
      return {
        isDisabled: true,
        reason: "Selecione um endereço para prosseguir.",
      };
    }

    if (Number(selectedAmount) <= 0) {
      return {
        isDisabled: true,
        reason: "Informe um valor válido para o pedido.",
      };
    }

    if (!productCart?.length) {
      return {
        isDisabled: true,
        reason: "Adicione ao menos um produto ao carrinho.",
      };
    }

    if (productCart.some((p) => !Number(p?.amount))) {
      return {
        isDisabled: true,
        reason: "Todos os produtos devem ter quantidade mínima acima de 1.",
      };
    }

    if (productCart.some((p) => !Number(p?.value))) {
      return {
        isDisabled: true,
        reason: `O produto ${
          productCart.find((p) => !Number(p?.value))?.name
        } esta com valor subtotal zerado.`,
      };
    }

    return { isDisabled: false, reason: null };
  }, [selectedAddress, selectedAmount, productCart]);
};
